import axiosInstance from "../axiosInstance";

/** Over this the request is rejected outright rather than truncated, so batches are capped here. */
export const MAX_TRIP_IDS_PER_REQUEST = 500;

/**
 * Fetches a trip-scoped travcom collection for a set of trips, working around a shared contract:
 * duplicate ids, an empty set, and going over the cap are each a 400 rather than a tolerated request.
 * Row order isn't guaranteed, so callers sort, and meta.totalItems describes one batch only.
 */
export default async function getByTripIds<T>(
  path: string,
  tripIds: readonly (number | string)[],
  batchSize: number = MAX_TRIP_IDS_PER_REQUEST,
): Promise<T[]> {
  // Deduped as text: trip ids are BIGINT / DECIMAL server-side and may arrive either way.
  const uniqueIds = [...new Set(tripIds.map(String))].filter(Boolean);
  if (uniqueIds.length === 0) return [];

  const perRequest = Math.min(Math.max(batchSize, 1), MAX_TRIP_IDS_PER_REQUEST);
  const batches: string[][] = [];
  for (let index = 0; index < uniqueIds.length; index += perRequest) {
    batches.push(uniqueIds.slice(index, index + perRequest));
  }

  const responses = await Promise.all(
    batches.map((ids) =>
      axiosInstance.get(path, {
        params: { tripIds: ids },
        // tripIds=101&tripIds=202, not axios's default tripIds[]=101.
        paramsSerializer: { indexes: null },
      }),
    ),
  );

  return responses.flatMap((res) => (res.data?.data as T[]) || []);
}
